import { useAppSelector } from "@store/store-helper";
import { useCallback, useMemo } from "react";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { hasUserValidFeatureCompanyLevel } from "@utils/feature-control/company/company-feature-control";
import { selectedCompanyContextSelector } from "@store/sdb-company/sdb-company-selector";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props {
  /** The internal name used in the UI to determine certain feature */
  featureName: RequiredFeatureCompanyLevelName;

  /** The available features of the company. */
  featuresAvailable?: FaroCoreApiTypes.Feature[];
}

export type ReturnProps = {
  [key in RequiredFeatureCompanyLevelName]: boolean;
} & {
  /** Wrapper to check if certain features are enabled and permitted to use by the user */
  hasFeatureCompanyLevel: ({
    featureName,
    featuresAvailable,
  }: Props) => boolean;
};

/** Checks whether a user has permission for an action on company level or not */
export function useHasUserValidFeatureCompanyLevel({
  featuresAvailable = undefined,
}: Pick<Props, "featuresAvailable"> = {}): ReturnProps {
  const companyContext = useAppSelector(selectedCompanyContextSelector);

  /** If user provided a featuresAvailable, then use that. Otherwise, use from companyContext */
  const availableFeatures = useMemo(() => {
    return featuresAvailable ?? companyContext?.featuresAvailable;
  }, [featuresAvailable, companyContext?.featuresAvailable]);

  const canViewCompanyGroups = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewCompanyGroups,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewCompanyMembers = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewCompanyMembers,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewProjects = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewProjects,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canCreateProjects = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canCreateProjects,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewAnalytics = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewAnalytics,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewPremiumAnalytics = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewIntegrations = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewIntegrations,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const canViewWorkspaceSettings = useMemo(
    () =>
      hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewWorkspaceSettings,
        featuresAvailable: availableFeatures,
      }),
    [availableFeatures]
  );

  const hasFeatureCompanyLevel = useCallback(
    ({ featureName, featuresAvailable }: Props) =>
      hasUserValidFeatureCompanyLevel({
        featureName,
        featuresAvailable: featuresAvailable ?? availableFeatures,
      }),
    [availableFeatures]
  );

  return {
    canViewCompanyGroups,
    canViewCompanyMembers,
    canViewProjects,
    canCreateProjects,
    canViewAnalytics,
    canViewPremiumAnalytics,
    canViewIntegrations,
    canViewWorkspaceSettings,
    hasFeatureCompanyLevel,
  };
}
